// Story 1.21 — TanStack Query hook for GET /api/v1/creators/applications/me.
//
// Drives the BecomeCreatorPage's status-driven render. Returns null
// (envelope.data === null) when the user has no application yet; the
// page renders the application form in that case.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type CreatorLinkedAccount = {
  kind: string;
  url: string;
  verified_at?: string;
};

export type CreatorApplicationStatus = {
  application_id: string;
  display_name: string;
  hello_text: string;
  image_signed_url: string | null;
  image_signed_url_expires_at: string | null;
  signature_signed_url?: string | null;
  linked_accounts: CreatorLinkedAccount[];
  region: string;
  language: string;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  can_resubmit: boolean;
};

export type CreatorApplicationStatusEnvelope = {
  data: CreatorApplicationStatus | null;
};

async function fetchApplicationStatus(): Promise<CreatorApplicationStatusEnvelope> {
  const response = await fetch("/api/v1/creators/applications/me", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`applications/me fetch failed: ${response.status}`);
  }
  return (await response.json()) as CreatorApplicationStatusEnvelope;
}

export function useCreatorApplicationStatus() {
  return useQuery({
    queryKey: ["creator-application-status"],
    queryFn: fetchApplicationStatus,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
